import { defineStore } from 'pinia'

export const useFormStore = defineStore('form', {
  state: () => ({
    // UI State
    formElement: 'membershipForm',
    currentStep: 1,
    applicationType: '', // 'new' or 'add'

    // Step validation tracking
    stepValidation: {
      step1: false,
      step2: false,
      step3: false,
      step4: false,
      step5: false,
      step6: false,
    },

    // TIN Search state (for 'add' applications)
    tinSearchResults: {
      tin: '',
      confirmedTin: '',
      groupName: null,
      isLoading: false,
      error: null,
      data: [],
      options: {
        practices: false,
        locations: false,
        providers: false,
      },
    },

    // Main form data - matches the final JSON structure exactly
    membershipApplicationInfo: {
      groupTin: '', // For 'add' applications
      groupLegalEntityInfo: {
        groupName: '',
        groupTIN: '',
        groupMSO: 'true',
        fullAddress: {
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
        },
        pointOfContact: {
          groupPocFirstName: '',
          groupPocLastName: '',
          groupPocPhone: '',
          groupPocExt: '',
          groupPocEmail: '',
        },
        contractApproval: {
          groupCapFirstName: '',
          groupCapLastName: '',
          groupCapPhone: '',
          groupCapExt: '',
          groupCapEmail: '',
          groupCapAddress: {
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
          },
        },
        physicianChampion: {
          championFirstName: '',
          championLastName: '',
          championPhone: '',
          championExt: '',
          championEmail: '',
        },
      },
      practices: [],
      providers: [],
    },

    // Final submission state
    isFormAccurate: false,
  }),

  getters: {
    // Dynamic step visibility based on application type and TIN search options
    requiredSteps() {
      if (this.applicationType === 'new') {
        return [1, 2, 3, 4, 5, 6]
      }
      if (this.applicationType === 'add') {
        let steps = [1, 2] // Application Type and Group Information always required

        if (this.tinSearchResults.options.practices) {
          steps.push(3) // Practices step
        }
        if (this.tinSearchResults.options.locations) {
          steps.push(4) // Locations step
        }
        if (this.tinSearchResults.options.providers) {
          steps.push(5) // Providers step
        }

        steps.push(6) // Agreement step always required
        return steps
      }

      return [1, 2, 3, 4, 5, 6] // Default to all steps
    },

    // Check if a specific step should be shown
    shouldShowStep() {
      return (stepNumber) => this.requiredSteps.includes(stepNumber)
    },

    // Check if current step is valid
    isCurrentStepValid() {
      return this.stepValidation[`step${this.currentStep}`] || false
    },

    // Check if all required steps are valid
    allStepsValid() {
      return this.requiredSteps.every((stepNum) => this.stepValidation[`step${stepNum}`])
    },

    // Get final submission data
    submissionData() {
      // Deep clone the data to avoid modifying the original
      const cleanData = JSON.parse(JSON.stringify(this.membershipApplicationInfo))

      // Clean up practices and locations - remove UI-specific properties
      if (cleanData.practices) {
        cleanData.practices.forEach((practice) => {
          // Remove UI-specific properties from practice
          delete practice.isFromTinSearch
          delete practice.isReadOnly

          // Clean up locations within each practice
          if (practice.locations) {
            practice.locations.forEach((location) => {
              delete location.isFromTinSearch
              delete location.isReadOnly
              delete location._originalPracticeId

              // Remove sameAsOfficeManager from billingManager
              if (location.billingManager) {
                delete location.billingManager.sameAsOfficeManager
              }
            })
          }
        })
      }

      // Clean up providers - remove UI-specific properties
      if (cleanData.providers) {
        cleanData.providers.forEach((provider) => {
          delete provider.isFromTinSearch
          delete provider.isReadOnly

          // Ensure degree is sent as an integer (Azure Function expects int)
          provider.degree = parseInt(provider.degree, 10) || 0
        })
      }

      // Rename groupTin to GroupTin to match Azure Function model casing
      if ('groupTin' in cleanData) {
        cleanData.GroupTin = cleanData.groupTin
        delete cleanData.groupTin
      }

      return {
        membershipApplicationInfo: cleanData,
      }
    },
  },

  actions: {
    // Generate consistent numeric IDs
    generateId() {
      return Math.floor(Math.random() * 900000000) + 100000000
    },

    // Generate string IDs for specific cases
    generateStringId() {
      return String(Math.floor(Math.random() * 900000000) + 100000000)
    },

    // Set application type and reset form if needed
    setApplicationType(type) {
      const previousType = this.applicationType
      this.applicationType = type

      // Reset step validation except step 1
      Object.keys(this.stepValidation).forEach((key) => {
        if (key !== 'step1') {
          this.stepValidation[key] = false
        }
      })
      this.stepValidation.step1 = Boolean(type)

      // Reset form data if changing types
      if (previousType && previousType !== type) {
        this.resetFormData()
      }

      // Initialize based on application type
      if (type === 'new') {
        this.initializeForNewApplication()
      } else if (type === 'add') {
        this.initializeForAddApplication()
      }
    },

    // Initialize for new application
    initializeForNewApplication() {
      this.clearTinSearchResults()
      this.ensurePracticeStructure()
    },

    // Initialize for add application
    initializeForAddApplication() {
      // Clear any previous form data except TIN search results
      this.membershipApplicationInfo.practices = []
      this.membershipApplicationInfo.providers = []
    },

    // Clear TIN search results
    clearTinSearchResults() {
      this.tinSearchResults = {
        tin: '',
        confirmedTin: '',
        groupName: null,
        isLoading: false,
        error: null,
        data: [],
        options: {
          practices: false,
          locations: false,
          providers: false,
        },
      }
    },

    // Ensure at least one practice exists for new applications
    ensurePracticeStructure() {
      if (this.membershipApplicationInfo.practices.length === 0) {
        this.addPractice()
      }
    },

    // Add a new practice
    addPractice() {
      const practice = {
        practiceId: this.generateId(),
        practiceName: '',
        locations: [],
      }
      this.membershipApplicationInfo.practices.push(practice)
      return practice
    },

    // Remove a practice
    removePractice(practiceId) {
      const index = this.membershipApplicationInfo.practices.findIndex(
        (p) => p.practiceId == practiceId,
      )
      if (index > -1) {
        this.membershipApplicationInfo.practices.splice(index, 1)

        // Remove any providers associated with this practice
        this.membershipApplicationInfo.providers.forEach((provider) => {
          provider.providerLocations = provider.providerLocations.filter(
            (loc) => loc.practiceId != practiceId,
          )
        })
      }
    },

    // Add a new location to a practice
    addLocation(practiceId) {
      const practice = this.membershipApplicationInfo.practices.find(
        (p) => p.practiceId == practiceId,
      )
      if (practice) {
        const location = {
          locationId: this.generateId(),
          locationName: '',
          phone: '',
          ext: '',
          fax: '',
          fullAddress: {
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
          },
          officeManager: {
            firstName: '',
            lastName: '',
            phone: '',
            ext: '',
            email: '',
          },
          billingManager: {
            sameAsOfficeManager: false,
            firstName: '',
            lastName: '',
            phone: '',
            ext: '',
            email: '',
          },
          emr: {
            vendor: '',
            version: '',
          },
          practiceId: String(practiceId),
        }
        practice.locations.push(location)
        return location
      }
    },

    // Remove a location
    removeLocation(practiceId, locationId) {
      const practice = this.membershipApplicationInfo.practices.find(
        (p) => p.practiceId == practiceId,
      )
      if (practice) {
        const index = practice.locations.findIndex((l) => l.locationId == locationId)
        if (index > -1) {
          practice.locations.splice(index, 1)

          // Remove any provider associations with this location
          this.membershipApplicationInfo.providers.forEach((provider) => {
            provider.providerLocations = provider.providerLocations.filter(
              (loc) => loc.locationId != locationId,
            )
          })
        }
      }
    },

    // Add a new provider
    addProvider() {
      const provider = {
        providerId: this.generateId(),
        firstName: '',
        middleInitial: '',
        lastName: '',
        suffix: '',
        alias: '',
        degree: '',
        npi: '',
        phone: '',
        email: '',
        dateOfBirth: '',
        primarySpecialty: '',
        secondarySpecialty: '',
        cms: false,
        isPrivleged: false,
        providerLocations: [],
      }
      this.membershipApplicationInfo.providers.push(provider)
      return provider
    },

    // Remove a provider
    removeProvider(providerId) {
      const index = this.membershipApplicationInfo.providers.findIndex(
        (p) => p.providerId == providerId,
      )
      if (index > -1) {
        this.membershipApplicationInfo.providers.splice(index, 1)
      }
    },

    // Populate data from TIN search results
    populateFromTinSearch() {
      if (!this.tinSearchResults.data.length) return

      // Set the confirmed TIN in the membership application info
      this.membershipApplicationInfo.groupTin = this.tinSearchResults.confirmedTin

      // Clear existing data
      this.membershipApplicationInfo.practices = []
      this.membershipApplicationInfo.providers = []

      // Populate practices
      if (
        this.tinSearchResults.options.practices ||
        this.tinSearchResults.options.locations ||
        this.tinSearchResults.options.providers
      ) {
        this.tinSearchResults.data.forEach((tinPractice) => {
          const practice = {
            practiceId: String(tinPractice.PracticeId || this.generateId()),
            practiceName: tinPractice.PracticeName || '',
            locations: [],
            isFromTinSearch: true,
            isReadOnly: true,
          }

          // Populate locations if locations option is selected OR if practice has location data available
          if ((this.tinSearchResults.options.locations || this.tinSearchResults.options.providers) && tinPractice.PracticeLocations) {
            tinPractice.PracticeLocations.forEach((tinLocation) => {
              const location = {
                locationId: String(tinLocation.LocationId || this.generateId()),
                locationName: tinLocation.LocationName || '',
                phone: '',
                ext: '',
                fax: '',
                fullAddress: {
                  address1: tinLocation.Address1 || '',
                  address2: tinLocation.Address2 || '',
                  city: tinLocation.City || '',
                  state: tinLocation.State || '',
                  zip: tinLocation.ZipCode || '',
                },
                officeManager: {
                  firstName: '',
                  lastName: '',
                  phone: '',
                  ext: '',
                  email: '',
                },
                billingManager: {
                  sameAsOfficeManager: false,
                  firstName: '',
                  lastName: '',
                  phone: '',
                  ext: '',
                  email: '',
                },
                emr: {
                  vendor: '',
                  version: '',
                },
                practiceId: practice.practiceId,
                isFromTinSearch: true,
                isReadOnly: true,
              }
              practice.locations.push(location)
            })
          } else if (this.tinSearchResults.options.providers && !tinPractice.PracticeLocations) {
            // If providers are selected but no location data is available from TIN search,
            // create a default location so that providers can be assigned to something in Step 5
            const defaultLocation = {
              locationId: String(this.generateId()),
              locationName: practice.practiceName || 'Main Location',
              phone: '',
              ext: '',
              fax: '',
              fullAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
              },
              officeManager: {
                firstName: '',
                lastName: '',
                phone: '',
                ext: '',
                email: '',
              },
              billingManager: {
                sameAsOfficeManager: false,
                firstName: '',
                lastName: '',
                phone: '',
                ext: '',
                email: '',
              },
              emr: {
                vendor: '',
                version: '',
              },
              practiceId: practice.practiceId,
              isFromTinSearch: true,
              isReadOnly: false, // Allow editing since this is a placeholder
            }
            practice.locations.push(defaultLocation)
          }

          this.membershipApplicationInfo.practices.push(practice)
        })
      }

      // Add empty practice for user input only for new applications or when no practices from TIN search
      if (this.applicationType === 'new' || !this.tinSearchResults.options.practices) {
        this.addPractice()
      }

      // Populate providers if option selected
      if (this.tinSearchResults.options.providers) {
        this.tinSearchResults.data.forEach((tinPractice) => {
          if (tinPractice.Providers) {
            tinPractice.Providers.forEach((tinProvider) => {
              const provider = {
                providerId: parseInt(tinProvider.ProviderId, 10) || this.generateId(),
                firstName: tinProvider.FirstName || '',
                middleInitial: tinProvider.MiddleInitial || '',
                lastName: tinProvider.LastName || '',
                suffix: tinProvider.Suffix || '',
                alias: tinProvider.Alias || '',
                degree: parseInt(tinProvider.Degree, 10) || 0,
                npi: tinProvider.NPI || '',
                phone: tinProvider.Phone || '',
                email: tinProvider.Email || '',
                dateOfBirth: tinProvider.DateOfBirth || '',
                primarySpecialty: String(tinProvider.PrimarySpecialty || ''),
                secondarySpecialty: String(tinProvider.SecondarySpecialty || ''),
                cms: Boolean(tinProvider.CMS),
                isPrivleged: Boolean(tinProvider.IsPrivileged),
                providerLocations: (tinProvider.Locations || []).map((loc) => ({
                  practiceId: String(tinPractice.PracticeId || ''),
                  locationId: String(loc.LocationId || ''),
                  isPrimary: loc.IsPrimary || false,
                })),
              }
              this.membershipApplicationInfo.providers.push(provider)
            })
          }
        })
      }
    },

    // Navigation methods
    setCurrentStep(step) {
      this.currentStep = step
    },

    setStepValidation(step, isValid) {
      this.stepValidation[`step${step}`] = isValid
    },

    nextStep() {
      const currentIndex = this.requiredSteps.indexOf(this.currentStep)
      if (currentIndex < this.requiredSteps.length - 1) {
        this.currentStep = this.requiredSteps[currentIndex + 1]
      }
    },

    prevStep() {
      const currentIndex = this.requiredSteps.indexOf(this.currentStep)
      if (currentIndex > 0) {
        this.currentStep = this.requiredSteps[currentIndex - 1]
      }
    },

    // Initialize application type from URL parameter
    initializeFromUrlParams() {
      const urlParams = new URLSearchParams(window.location.search)
      const appParam = urlParams.get('app')

      if (appParam === 'add') {
        this.setApplicationType('add')
      } else {
        // Default to 'new' for no param or invalid value
        this.setApplicationType('new')
      }
    },

    // Reset form data
    resetFormData() {
      this.membershipApplicationInfo = {
        groupTin: '',
        groupLegalEntityInfo: {
          groupName: '',
          groupTIN: '',
          groupMSO: 'true',
          fullAddress: {
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
          },
          pointOfContact: {
            groupPocFirstName: '',
            groupPocLastName: '',
            groupPocPhone: '',
            groupPocExt: '',
            groupPocEmail: '',
          },
          contractApproval: {
            groupCapFirstName: '',
            groupCapLastName: '',
            groupCapPhone: '',
            groupCapExt: '',
            groupCapEmail: '',
            groupCapAddress: {
              address1: '',
              address2: '',
              city: '',
              state: '',
              zip: '',
            },
          },
          physicianChampion: {
            championFirstName: '',
            championLastName: '',
            championPhone: '',
            championExt: '',
            championEmail: '',
          },
        },
        practices: [],
        providers: [],
      }
      this.isFormAccurate = false
    },
  },
})
